<template>
  <!-- Add a risk scenario to an asset in three steps (US 9370). Inherent risk is the main step here;
       afterwards only the residual risk is updated. -->
  <V2SidePanel ref="panel"
               :kind="$t('risk_tab.kind')"
               :title="$t('risk_tab.add')"
               :busy="saving"
               wide
               qid="risk-add-panel"
               @hidden="reset">
    <template #head>
      <div class="sx-steps">
        <span v-for="(label, index) in steps" :key="index" :class="{ done: step > index + 1, cur: step === index + 1 }">{{ index + 1 }} {{ label }}</span>
      </div>
    </template>
    <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>

    <!-- 1. Scenario and method -->
    <template v-if="step === 1">
      <div class="v2-field">
        <span class="v2-field__label">{{ $t('risk_tab.scenario') }} <span class="v2-field__req">*</span></span>
        <b-form-input v-model="keyword" type="search" :placeholder="$t('risk_tab.search')" qid="risk-add-search" @input="search"></b-form-input>
        <div class="sx-options">
          <button v-for="scenario in catalogue"
                  :key="scenario.id"
                  type="button"
                  class="sx-option"
                  :class="{ on: selected && selected.id === scenario.id }"
                  :disabled="linked.includes(scenario.id)"
                  :qid="'risk-add-option-' + scenario.id"
                  @click="selected = scenario">
            <font-awesome-icon icon="exclamation-triangle"/>
            <span class="sx-option__grow">
              {{ scenario.name }}
              <small>{{ optionMeta(scenario) }}</small>
            </span>
            <font-awesome-icon v-if="selected && selected.id === scenario.id" icon="check"/>
          </button>
          <p v-if="loaded && !catalogue.length" class="sx-options__empty">{{ $t('risk_tab.no_catalogue') }}</p>
        </div>
      </div>
      <div class="v2-field">
        <span class="v2-field__label">{{ $t('risk_tab.method') }}</span>
        <div class="v2-field__two">
          <button v-for="value in ['single_risk', 'cia_risk']" :key="value" type="button" class="sx-option" :class="{ on: method === value }" :qid="'risk-add-method-' + value" @click="method = value">
            <span class="sx-option__grow">{{ $t('risk_tab.methods.' + value) }}<small>{{ $t('risk_tab.method_hints.' + value) }}</small></span>
            <font-awesome-icon v-if="method === value" icon="check"/>
          </button>
        </div>
        <small class="v2-field__note">{{ $t('risk_tab.method_default', { method: $t('risk_tab.methods.' + defaultMethod) }) }}</small>
      </div>
      <b-form-checkbox v-if="getClient.gdprModule" v-model="privacy" qid="risk-add-privacy">{{ $t('risk_tab.privacy_included') }}</b-form-checkbox>
    </template>

    <!-- 2. Inherent risk -->
    <template v-if="step === 2">
      <div class="sx-result">
        <font-awesome-icon icon="exclamation-triangle"/>
        <div class="sp-grow"><b>{{ selected.name }}</b> · {{ $t('risk_tab.methods.' + method) }}<br><small>{{ $t('risk_tab.inherent_intro') }}</small></div>
        <button type="button" class="v2-link" @click="step = 1">{{ $t('risk_tab.change') }}</button>
      </div>
      <div v-for="type in types" :key="type" class="v2-field">
        <span class="v2-field__label">{{ $t('risk_tab.inherent') }} <span v-if="types.length > 1" class="v2-field__hint">· {{ $t('risk_tab.types.' + type) }}</span></span>
        <RiskRatingForm v-model="inherent[type]" :scale="scale" :method="method" :matrix="true" :qid="'risk-add-inherent-' + type"></RiskRatingForm>
        <div v-if="scoreOf(inherent[type])" class="rk-result" :class="'is-' + band(scale, scoreOf(inherent[type]))">
          {{ $t('risk_tab.inherent') }} <RiskScore small :value="scoreOf(inherent[type])" :band="band(scale, scoreOf(inherent[type]))"></RiskScore>
          {{ $t('risk_tab.bands.' + band(scale, scoreOf(inherent[type]))) }}
        </div>
      </div>
      <div class="v2-field">
        <span class="v2-field__label">{{ $t('risk_tab.consequences') }} <span class="v2-field__hint">· {{ $t('risk_tab.optional') }}</span></span>
        <b-form-textarea v-model="consequences" rows="2" max-rows="6" :placeholder="$t('risk_tab.consequences_placeholder')"></b-form-textarea>
      </div>
    </template>

    <!-- 3. Residual risk now -->
    <template v-if="step === 3">
      <div class="v2-field">
        <span class="v2-field__label">{{ $t('risk_tab.residual_now') }}</span>
        <button type="button" class="sx-option" :class="{ on: sameAsInherent }" qid="risk-add-same" @click="sameAsInherent = true">
          <span class="sx-option__grow">{{ $t('risk_tab.same_as_inherent') }}<small>{{ $t('risk_tab.same_as_inherent_hint') }}</small></span>
          <font-awesome-icon v-if="sameAsInherent" icon="check"/>
        </button>
        <button type="button" class="sx-option" :class="{ on: !sameAsInherent }" qid="risk-add-own" @click="sameAsInherent = false">
          <span class="sx-option__grow">{{ $t('risk_tab.set_residual') }}<small>{{ $t('risk_tab.set_residual_hint') }}</small></span>
          <font-awesome-icon v-if="!sameAsInherent" icon="check"/>
        </button>
      </div>
      <template v-if="!sameAsInherent">
        <div v-for="type in types" :key="type" class="v2-field">
          <span class="v2-field__label">{{ $t('risk_tab.residual') }} <span v-if="types.length > 1" class="v2-field__hint">· {{ $t('risk_tab.types.' + type) }}</span></span>
          <RiskRatingForm v-model="residual[type]" :scale="scale" :method="method" :matrix="true" :qid="'risk-add-residual-' + type"></RiskRatingForm>
        </div>
      </template>
    </template>

    <template #foot>
      <b-button v-if="step > 1" variant="secondary" @click="step--"><font-awesome-icon icon="angle-left"/> {{ $t('risk_tab.back') }}</b-button>
      <span class="v2-panel__grow"></span>
      <b-button v-if="step < 3" variant="success" :disabled="!canContinue" qid="risk-add-next" @click="step++">
        {{ $t('risk_tab.next') }} <font-awesome-icon icon="angle-right"/>
      </b-button>
      <b-button v-else variant="success" :disabled="saving || !canFinish" qid="risk-add-save" @click="save">
        <font-awesome-icon :icon="saving ? 'spinner' : 'check'" :class="{ 'fa-spin': saving }"/> {{ $t('risk_tab.add') }}
      </b-button>
    </template>
  </V2SidePanel>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';
  import V2SidePanel from '@/components/V2SidePanel.vue';
  import RiskRatingForm from '@/components/risk/RiskRatingForm.vue';
  import RiskScore from '@/components/risk/RiskScore.vue';
  import {band, score, ciaScore, CIA, METHOD_CIA} from '@/components/risk/riskScale.js';

  const TYPE_VALUES = { business: 'business risk', privacy: 'privacy risk' };

  export default {
    name: 'AddRiskScenarioPanel',
    components: {
      V2SidePanel,
      RiskRatingForm,
      RiskScore
    },
    props: {
      asset: { type: String, required: true },
      object: { type: Object, required: true },
      scale: { type: Object, required: true },
      defaultMethod: { type: String, default: 'single_risk' },
      catalogueType: { type: String, required: true },
      // The workspace whose catalogue applies
      client: { type: String, default: null },
      // Catalogue scenarios the asset has already
      linked: { type: Array, default: () => [] }
    },
    data: function() {
      return {
        step: 1,
        keyword: '',
        catalogue: [],
        loaded: false,
        selected: null,
        method: 'single_risk',
        privacy: false,
        inherent: {},
        residual: {},
        consequences: '',
        sameAsInherent: true,
        created: false,
        saving: false,
        error: '',
        searchTimer: null
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      steps() {
        return [this.$t('risk_tab.steps.scenario'), this.$t('risk_tab.steps.inherent'), this.$t('risk_tab.steps.residual')];
      },
      types() {
        return this.privacy ? ['business', 'privacy'] : ['business'];
      },
      canContinue() {
        if (this.step === 1) {
          return !!this.selected;
        }
        return this.types.every(type => this.scoreOf(this.inherent[type]));
      },
      canFinish() {
        return this.sameAsInherent || this.types.every(type => this.scoreOf(this.residual[type]));
      }
    },
    watch: {
      // A new method needs empty ratings of its own shape
      method: function() {
        this.inherent = this.emptyRatings();
        this.residual = this.emptyRatings();
      }
    },
    methods: {
      band: band,
      show() {
        this.reset();
        this.loadCatalogue();
        this.$refs.panel.show();
      },
      reset() {
        Object.assign(this, { step: 1, keyword: '', selected: null, method: this.defaultMethod, privacy: false, consequences: '',
          sameAsInherent: true, created: false, error: '', saving: false });
        this.inherent = this.emptyRatings();
        this.residual = this.emptyRatings();
      },
      emptyRatings() {
        let empty = () => this.method === METHOD_CIA ? { confidentiality: {}, integrity: {}, availability: {} } : { impact: null, probability: null };
        return { business: empty(), privacy: empty() };
      },
      scoreOf(rating) {
        if (!rating) {
          return null;
        }
        return this.method === METHOD_CIA ? ciaScore(this.scale, rating) : score(this.scale, rating.impact, rating.probability);
      },
      optionMeta(scenario) {
        let parts = [];
        if (scenario.risk_category && scenario.risk_category.name) {
          parts.push(scenario.risk_category.name);
        }
        if (this.linked.includes(scenario.id)) {
          parts.push(this.$t('system_link.already'));
        }
        return parts.join(' · ');
      },
      search() {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(this.loadCatalogue, 300);
      },
      loadCatalogue() {
        let self = this;
        piincHttp.get('risk-scenarios', { params: {
          client: this.client || this.getClient.slug,
          per_page: 50,
          types: [this.catalogueType],
          keyword: this.keyword || undefined
        } }).then(function(response) {
          self.catalogue = response.data.items;
          self.loaded = true;
        }, function() {});
      },
      // Fields of a rating on the scenario link (inherent risk)
      inherentFields(type, rating) {
        let data = {};
        if (this.method === METHOD_CIA) {
          CIA.forEach(key => {
            data[type + '_' + key + '_risk_impact'] = rating[key].impact || null;
            data[type + '_' + key + '_risk_probability'] = rating[key].probability || null;
          });
        } else {
          data[type + '_inherent_risk_impact'] = rating.impact;
          data[type + '_inherent_risk_probability'] = rating.probability;
        }
        return data;
      },
      // Fields of a residual risk entry
      residualFields(type, rating) {
        let data = { type: TYPE_VALUES[type] };
        if (this.method === METHOD_CIA) {
          CIA.forEach(key => {
            data[key + '_impact'] = rating[key].impact || null;
            data[key + '_probability'] = rating[key].probability || null;
          });
        } else {
          data.impact = rating.impact;
          data.probability = rating.probability;
        }
        return data;
      },
      save() {
        let self = this;
        let data = {
          risk_scenario_id: this.selected.id,
          risk_method: this.method,
          privacy_included: this.privacy ? 1 : 0,
          business_description: this.consequences || null
        };
        this.types.forEach(type => Object.assign(data, this.inherentFields(type, this.inherent[type])));

        this.saving = true;
        this.error = '';
        piincHttp.post(this.asset + '/' + this.object.slug + '/risk-scenarios', data)
          .then(function() {
            return piincHttp.get('risk/' + self.asset + '/' + self.object.id, { params: { client: self.getClient.slug } });
          })
          .then(function(response) {
            let added = response.data.scenarios.find(item => item.scenario && item.scenario.id === self.selected.id);
            // The log starts with the residual risk now; the same as the inherent risk until measures are in place
            let ratings = self.sameAsInherent ? self.inherent : self.residual;
            self.created = true;
            // One after the other: both entries change the same scenario's "latest" flags
            return self.types.reduce((chain, type) => chain.then(() => piincHttp.post('risk/' + self.asset + '/' + self.object.id + '/scenarios/' + added.link_id + '/residual',
              Object.assign(self.residualFields(type, ratings[type]), { description: self.sameAsInherent ? self.$t('risk_tab.first_entry') : null }))), Promise.resolve());
          })
          .then(function() {
            self.saving = false;
            self.$toastr('success', self.$t('risk_tab.added'));
            self.$refs.panel.hide();
            self.$emit('added');
          }, function(error) {
            self.saving = false;
            self.error = (error && error.data && error.data.message) || self.$t('system_hero.panel.error');
            // The scenario itself was added, so the list shows it
            if (self.created) {
              self.$emit('added');
            }
          });
      }
    }
  }
</script>
